import React, { useState } from 'react';
import QuickCard from './QuickCard';
import { Button } from './ui/button';
import { ArrowLeft, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * TestQuickCard - Test page for QuickCard prototype
 * 
 * Access at: /test-quickcard
 * 
 * Uses mock data to test the QuickCard component without
 * affecting the main app flows.
 */
const TestQuickCard = () => {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  
  // Mock configuration state
  const [config, setConfig] = useState({
    holes: 9,
    startingNine: 'back',
    eventType: 'skins',
    skinValue: 5,
    playerCount: 4
  });
  
  // Mock players
  const mockPlayers = [
    { player_id: 'p1', name: 'Chad Sano', handicap: 18 },
    { player_id: 'p2', name: 'Mike Johnson', handicap: 12 },
    { player_id: 'p3', name: 'Dave Wilson', handicap: 8 },
    { player_id: 'p4', name: 'Tom Brady', handicap: 22 },
  ].slice(0, config.playerCount);
  
  // Mock course data - Pine Valley Back 9
  const mockHolePars = [4, 3, 5, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4, 4, 3, 5, 4, 4];
  const mockHoleHandicaps = [11, 17, 3, 7, 9, 15, 1, 5, 13, 12, 18, 4, 8, 10, 16, 2, 6, 14];
  
  // Scores state (for testing persistence)
  const [scores, setScores] = useState({});
  
  const handleScoresUpdate = (newScores) => {
    console.log('Scores updated:', newScores);
    setScores(newScores);
  };
  
  const handleHoleComplete = (hole, holeScores) => {
    console.log(`Hole ${hole} complete:`, holeScores);
  };
  
  return (
    <div className="min-h-screen bg-stone-100">
      {/* Test Header */}
      <div className="bg-amber-500 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold">QuickCard Prototype</h1>
            <p className="text-xs text-amber-100">Test Mode - Not connected to real data</p>
          </div>
        </div>
        <button 
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 hover:bg-amber-600 rounded-lg"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white border-b p-4 space-y-3">
          <h3 className="font-bold text-sm text-stone-600">Test Configuration</h3>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Holes */}
            <div>
              <label className="text-xs text-stone-500">Holes</label>
              <select 
                value={config.holes}
                onChange={(e) => setConfig({...config, holes: parseInt(e.target.value)})}
                className="w-full border rounded p-2 text-sm"
              >
                <option value={9}>9 Holes</option>
                <option value={18}>18 Holes</option>
              </select>
            </div>
            
            {/* Starting Nine */}
            <div>
              <label className="text-xs text-stone-500">Starting Nine</label>
              <select 
                value={config.startingNine}
                onChange={(e) => setConfig({...config, startingNine: e.target.value})}
                className="w-full border rounded p-2 text-sm"
              >
                <option value="front">Front 9 (1-9)</option>
                <option value="back">Back 9 (10-18)</option>
              </select>
            </div>
            
            {/* Event Type */}
            <div>
              <label className="text-xs text-stone-500">Event Type</label>
              <select 
                value={config.eventType}
                onChange={(e) => setConfig({...config, eventType: e.target.value})}
                className="w-full border rounded p-2 text-sm"
              >
                <option value="skins">Skins Game</option>
                <option value="stroke_play">Stroke Play</option>
              </select>
            </div>
            
            {/* Player Count */}
            <div>
              <label className="text-xs text-stone-500">Players</label>
              <select 
                value={config.playerCount}
                onChange={(e) => setConfig({...config, playerCount: parseInt(e.target.value)})}
                className="w-full border rounded p-2 text-sm"
              >
                <option value={2}>2 Players</option>
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
              </select>
            </div>
            
            {/* Skin Value */}
            {config.eventType === 'skins' && (
              <div>
                <label className="text-xs text-stone-500">Skin Value</label>
                <select 
                  value={config.skinValue}
                  onChange={(e) => setConfig({...config, skinValue: parseInt(e.target.value)})}
                  className="w-full border rounded p-2 text-sm"
                >
                  <option value={1}>$1</option>
                  <option value={2}>$2</option>
                  <option value={5}>$5</option>
                  <option value={10}>$10</option>
                  <option value={20}>$20</option>
                </select>
              </div>
            )}
          </div>
          
          <Button 
            onClick={() => {
              setScores({});
              setShowSettings(false);
            }}
            variant="outline"
            size="sm"
          >
            Reset Scores
          </Button>
        </div>
      )}
      
      {/* QuickCard Component */}
      <QuickCard
        players={mockPlayers}
        holes={config.holes}
        startingNine={config.startingNine}
        holePars={mockHolePars}
        holeHandicaps={mockHoleHandicaps}
        courseName="Pine Valley CC (Test)"
        eventType={config.eventType}
        skinValue={config.skinValue}
        onScoresUpdate={handleScoresUpdate}
        onHoleComplete={handleHoleComplete}
        initialScores={scores}
        enableVoiceReadback={true}
      />
      
      {/* Debug Panel */}
      <div className="p-3">
        <details className="bg-white rounded-lg border">
          <summary className="p-3 font-bold text-sm text-stone-600 cursor-pointer">
            Debug: Current Scores
          </summary>
          <pre className="p-3 text-xs bg-stone-50 overflow-x-auto">
            {JSON.stringify(scores, null, 2)}
          </pre>
        </details>
      </div>
      
      {/* Instructions */}
      <div className="p-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <h4 className="font-bold mb-1">How to Test:</h4>
          <ol className="list-decimal ml-4 space-y-1 text-xs">
            <li>Tap the microphone button</li>
            <li>Say: "Chad got a 4, Mike got a 5, Dave got a 4, Tom got a 6"</li>
            <li>Wait for the readback confirmation</li>
            <li>Tap "Confirm & Next" to record and move to next hole</li>
            <li>Watch the scorecard fill in below</li>
          </ol>
          <p className="mt-2 text-xs text-blue-600">
            Note: Voice processing requires the backend group transcribe endpoint.
            Manual entry fallback coming soon.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestQuickCard;
